import React, { useEffect, useState } from "react";
import { Loader2, Mail, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { ReportsApi, errorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/** Send the current report selection to accountants as PDF/CSV attachments. */
export const EmailReportDialog = ({ open, onOpenChange, filters, onSent }) => {
  const { t } = useI18n();
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState("");
  const [formats, setFormats] = useState(["pdf", "csv"]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    ReportsApi.recipients()
      .then((d) => {
        setPeople(d.people || []);
        const accountants = (d.people || []).filter((p) => p.role === "accountant");
        const saved = (d.monthly_report?.recipients || []).filter(Boolean);
        setSelected(saved.length ? saved : accountants.map((a) => a.email));
      })
      .catch(() => setPeople([]));
  }, [open]);

  const toggle = (email) =>
    setSelected((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  const addExtra = () => {
    const value = extra.trim().toLowerCase();
    if (!value || !value.includes("@")) return;
    setSelected((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setExtra("");
  };

  const send = async () => {
    if (!selected.length) {
      toast.error(t("reports.addRecipient"));
      return;
    }
    setBusy(true);
    try {
      const res = await ReportsApi.email({
        recipients: selected,
        formats,
        message: message || null,
        date_from: filters?.date_from || null,
        date_to: filters?.date_to || null,
        user_id: filters?.user_id || null,
        category: filters?.category || null,
        status: filters?.status === "approved" ? "approved" : null,
      });
      toast.success(t("reports.sentToast", { count: selected.length }), {
        description: `${res.row_count} · ${res.period}`,
      });
      onSent && onSent(res);
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e, "Could not send the report"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="email-report-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("reports.emailTitle")}</DialogTitle>
          <DialogDescription>{t("reports.emailSub")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">{t("common.recipients")}</Label>
            <div className="space-y-1.5">
              {people.map((p) => (
                <label
                  key={p.email}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-sm"
                >
                  <Checkbox
                    checked={selected.includes(p.email)}
                    onCheckedChange={() => toggle(p.email)}
                    data-testid={`report-recipient-${p.role}`}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {p.name} <span className="text-muted-foreground">· {p.email}</span>
                  </span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {p.role === "accountant" ? t("common.accountantRole") : t("common.admin")}
                  </span>
                </label>
              ))}
              {selected
                .filter((e) => !people.some((p) => p.email === e))
                .map((e) => (
                  <div key={e} className="flex items-center gap-2 rounded-lg border p-2 text-sm">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{e}</span>
                    <button type="button" onClick={() => toggle(e)} aria-label="Remove">
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </div>
                ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={t("reports.addRecipient")}
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addExtra();
                  }
                }}
                data-testid="report-extra-recipient-input"
              />
              <Button type="button" variant="outline" onClick={addExtra} data-testid="report-add-recipient">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">{t("reports.attachments")}</Label>
            <div className="flex gap-3">
              {["pdf", "csv"].map((f) => (
                <label key={f} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={formats.includes(f)}
                    onCheckedChange={(v) =>
                      setFormats((prev) => (v ? [...new Set([...prev, f])] : prev.filter((x) => x !== f)))
                    }
                    data-testid={`report-format-${f}`}
                  />
                  {f.toUpperCase()}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="report-message" className="text-xs">
              {t("common.message")} ({t("common.optional")})
            </Label>
            <Textarea
              id="report-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="report-message-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={send} disabled={busy || !formats.length} data-testid="report-send-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            {busy ? t("common.sending") : t("common.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailReportDialog;
